import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Scale,
  FileText,
  Users,
  Calendar,
  MessageSquare,
  UserPlus,
  Briefcase,
  LogOut,
  Menu,
  X,
  Clock,
  BarChart3,
  DollarSign,
  Video,
  Table,
  Settings,
  BookOpen,
  Inbox,
  ClipboardList,
  PieChart,
  Mail,
  CheckCircle,
  AlertCircle,
  Loader,
  MapPin,
  UserCheck,
  PenTool,
  RefreshCw,
  AlertTriangle,
  Bell,
  User,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import NotificationBell from '../components/NotificationBell';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [user, setUser] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [attendanceAlerts, setAttendanceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchAdminStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/dashboard/admin-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/attendance/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { alerts: [] } }))
      ]);
      setAdminStats(statsRes.data);
      setAttendanceAlerts(alertsRes.data.alerts || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchAdminStats();
    
    // تحديث الإحصائيات كل 30 ثانية
    const interval = setInterval(fetchAdminStats, 30000);
    return () => clearInterval(interval);
  }, [fetchAdminStats]);

  // تحديث heartbeat للمستخدم
  useEffect(() => {
    const updateHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/user-sessions/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Heartbeat error:', err);
      }
    };
    
    updateHeartbeat();
    const interval = setInterval(updateHeartbeat, 60000); // كل دقيقة
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatLocation = (location) => {
    if (!location) return '-';
    if (location.address) return location.address;
    return `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`;
  };

  const menuItems = [
    { name: t('nav.dashboard'), path: '/dashboard', icon: Scale },
    { name: t('email.title'), path: '/emails/inbox', icon: Mail },
    { name: t('requests.title'), path: '/admin/client-requests', icon: Inbox },
    { name: t('library.title'), path: '/legal-library', icon: BookOpen },
    { name: t('nav.workSchedule'), path: '/work-schedules', icon: ClipboardList },
    { name: t('reports.title'), path: '/task-reports', icon: PieChart },
    { name: t('cases.title'), path: '/cases-table', icon: Table },
    { name: t('nav.cases'), path: '/cases', icon: FileText },
    { name: t('nav.clients'), path: '/clients', icon: Users },
    { name: t('nav.employees'), path: '/employees', icon: Briefcase },
    { name: t('schedule.title') || 'إدارة دوام الموظفين', path: '/employee-schedule', icon: Clock },
    { name: t('requests.assignTo'), path: '/assignments', icon: MessageSquare },
    { name: t('nav.appointments'), path: '/appointments', icon: Calendar },
    { name: t('nav.meetings'), path: '/meetings', icon: Video },
    { name: t('meetings.videoMeetings') || 'اجتماعات الفيديو', path: '/video-meetings', icon: Video },
    { name: t('nav.consultations'), path: '/consultations', icon: MessageSquare },
    { name: t('attendance.title'), path: '/attendance', icon: Clock },
    { name: t('nav.settings'), path: '/work-schedule', icon: Settings },
    { name: t('settings.accountSettings') || 'إعدادات الحساب', path: '/account-settings', icon: User },
    { name: t('reports.title'), path: '/reports', icon: BarChart3 },
    { name: t('nav.fees'), path: '/fees', icon: DollarSign },
    { name: t('vouchers.title'), path: '/vouchers', icon: FileText },
    { name: t('reports.financialReport'), path: '/financial-reports', icon: BarChart3 },
    { name: t('admin.newClient'), path: '/create-user', icon: UserPlus },
  ];

  // مكون بطاقة الإحصائيات التفصيلية
  const StatCard = ({ title, icon: Icon, iconColor, stats, onClick }) => (
    <div 
      onClick={onClick}
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:border-gold' : ''}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${iconColor}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h4 className="font-heading font-semibold text-primary text-xl">{title}</h4>
        </div>
        <span className="text-3xl font-bold text-primary font-heading">{stats.total}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-yellow-50 rounded-lg p-3">
          <span className="block text-2xl font-bold text-yellow-600">{stats.new}</span>
          <span className="text-sm text-yellow-700">جديدة</span>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <span className="block text-2xl font-bold text-blue-600">{stats.in_progress}</span>
          <span className="text-sm text-blue-700">تحت الإجراء</span>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <span className="block text-2xl font-bold text-green-600">{stats.completed}</span>
          <span className="text-sm text-green-700">منتهية</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand" dir={dir}>
      {/* Top Header Bar with Notification */}
      <div className={`fixed top-0 ${isRTL ? 'left-0 right-0 lg:right-56' : 'left-0 right-0 lg:left-56'} bg-primary/95 backdrop-blur-sm z-30 h-14 flex items-center justify-between px-4 border-b border-white/10`}>
        <div className="flex items-center gap-2">
          <span className="text-white/80 text-base font-body">{t('msg.welcome')}، {user?.full_name}</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector variant="minimal" className="text-white" />
          <NotificationBell />
        </div>
      </div>

      {/* Mobile Menu Toggle */}
      <div className={`lg:hidden fixed top-14 ${isRTL ? 'left-4' : 'right-4'} z-50`}>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-primary text-white rounded-md"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed ${isRTL ? 'right-0' : 'left-0'} top-0 h-full bg-primary w-56 transition-transform duration-300 z-40 overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : isRTL ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Logo size="sm" />
            <h1 className="text-base font-heading font-bold text-white">
              {t('admin.title')}
            </h1>
          </div>

          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`menu-${item.path.substring(1)}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200 font-body text-sm"
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full mt-4 flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-red-600 rounded-md transition-all duration-200 font-body text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:mr-56 p-6 pt-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-1">
                لوحة التحكم الشاملة
              </h2>
              <p className="text-base text-gray-500 font-body">
                آخر تحديث: {lastRefresh.toLocaleTimeString('ar-SA')}
              </p>
            </div>
            <button
              onClick={fetchAdminStats}
              className="flex items-center gap-2 px-5 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all font-body text-base"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </button>
          </div>

          {/* تنبيهات الحضور */}
          {attendanceAlerts.length > 0 && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                <h4 className="font-heading font-semibold text-yellow-800 text-lg">
                  تنبيهات الحضور ({attendanceAlerts.length})
                </h4>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {attendanceAlerts.slice(0, 5).map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 p-3 rounded-lg text-base ${
                      alert.severity === 'warning' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {alert.type === 'late_arrival' && <Clock className="w-5 h-5 flex-shrink-0" />}
                    {alert.type === 'overtime' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                    {alert.type === 'missing_clock_out' && <Bell className="w-5 h-5 flex-shrink-0" />}
                    <span className="font-body">{alert.message}</span>
                  </div>
                ))}
                {attendanceAlerts.length > 5 && (
                  <button 
                    onClick={() => navigate('/attendance')}
                    className="text-base text-yellow-700 hover:text-yellow-900 font-body"
                  >
                    عرض الكل ({attendanceAlerts.length})
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader className="w-8 h-8 text-gold animate-spin" />
            </div>
          ) : adminStats && (
            <>
              {/* إحصائيات الخدمات */}
              <div className="mb-8">
                <h3 className="text-2xl font-heading font-semibold text-primary mb-4 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-gold" />
                  إحصائيات الخدمات
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <StatCard
                    title="القضايا"
                    icon={FileText}
                    iconColor="bg-blue-500"
                    stats={adminStats.cases}
                    onClick={() => navigate('/admin/client-requests')}
                  />
                  <StatCard
                    title="الاستشارات"
                    icon={MessageSquare}
                    iconColor="bg-green-500"
                    stats={adminStats.consultations}
                    onClick={() => navigate('/consultations')}
                  />
                  <StatCard
                    title="استشارات الزوار"
                    icon={Users}
                    iconColor="bg-orange-500"
                    stats={adminStats.guest_consultations}
                    onClick={() => navigate('/guest-consultations')}
                  />
                  <StatCard
                    title="خدمات الموثق"
                    icon={PenTool}
                    iconColor="bg-purple-500"
                    stats={adminStats.notary}
                    onClick={() => navigate('/admin/client-requests')}
                  />
                </div>
              </div>

              {/* إحصائيات المهام */}
              <div className="mb-8">
                <h3 className="text-2xl font-heading font-semibold text-primary mb-4 flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-gold" />
                  إحصائيات المهام
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading font-semibold text-primary text-xl">إجمالي المهام</h4>
                      <span className="text-3xl font-bold text-primary">{adminStats.tasks.total}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center mb-4">
                      <div className="bg-yellow-50 rounded-lg p-3">
                        <span className="block text-2xl font-bold text-yellow-600">{adminStats.tasks.new}</span>
                        <span className="text-sm text-yellow-700">جديدة</span>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <span className="block text-2xl font-bold text-blue-600">{adminStats.tasks.in_progress}</span>
                        <span className="text-sm text-blue-700">تحت الإجراء</span>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <span className="block text-2xl font-bold text-green-600">{adminStats.tasks.completed}</span>
                        <span className="text-sm text-green-700">منتهية</span>
                      </div>
                    </div>
                    <div className="flex gap-4 text-base">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-600">من العملاء: <strong>{adminStats.tasks.from_clients}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-green-500" />
                        <span className="text-gray-600">من الموظفين: <strong>{adminStats.tasks.from_employees}</strong></span>
                      </div>
                    </div>
                  </div>
                  
                  {/* ملخص سريع */}
                  <div className="bg-gradient-to-br from-primary to-primary-800 rounded-xl p-6 text-white">
                    <h4 className="font-heading font-semibold mb-4 text-xl">ملخص سريع</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-lg p-4 text-center">
                        <Briefcase className="w-7 h-7 mx-auto mb-2 text-gold" />
                        <span className="block text-3xl font-bold">{adminStats.total_employees}</span>
                        <span className="text-base text-white/80">إجمالي الموظفين</span>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4 text-center">
                        <Users className="w-7 h-7 mx-auto mb-2 text-gold" />
                        <span className="block text-3xl font-bold">{adminStats.total_clients}</span>
                        <span className="text-base text-white/80">إجمالي العملاء</span>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4 text-center">
                        <UserCheck className="w-7 h-7 mx-auto mb-2 text-green-400" />
                        <span className="block text-3xl font-bold">{adminStats.online_employees_count}</span>
                        <span className="text-base text-white/80">موظفين أونلاين</span>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4 text-center">
                        <UserCheck className="w-7 h-7 mx-auto mb-2 text-blue-400" />
                        <span className="block text-3xl font-bold">{adminStats.online_clients_count}</span>
                        <span className="text-base text-white/80">عملاء متصلين</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* المتواجدين أونلاين */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* الموظفين المتواجدين */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h4 className="font-heading font-semibold text-primary mb-4 flex items-center gap-2 text-xl">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    الموظفين المتواجدين ({adminStats.online_employees_count})
                  </h4>
                  {adminStats.online_employees.length === 0 ? (
                    <p className="text-gray-500 text-base text-center py-4">لا يوجد موظفين متواجدين حالياً</p>
                  ) : (
                    <div className="space-y-3 max-h-56 overflow-y-auto">
                      {adminStats.online_employees.map((emp, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-green-50 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white text-base font-bold">
                              {emp.user_name?.charAt(0)}
                            </div>
                            <span className="font-body text-base">{emp.user_name}</span>
                          </div>
                          <span className="text-sm text-green-600">
                            دخول: {formatTime(emp.clock_in)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* العملاء المتصلين */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h4 className="font-heading font-semibold text-primary mb-4 flex items-center gap-2 text-xl">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    العملاء المتصلين ({adminStats.online_clients_count})
                  </h4>
                  {adminStats.online_clients.length === 0 ? (
                    <p className="text-gray-500 text-base text-center py-4">لا يوجد عملاء متصلين حالياً</p>
                  ) : (
                    <div className="space-y-3 max-h-56 overflow-y-auto">
                      {adminStats.online_clients.map((client, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-base font-bold">
                              {client.user_name?.charAt(0)}
                            </div>
                            <span className="font-body text-base">{client.user_name}</span>
                          </div>
                          <span className="text-sm text-blue-600">نشط</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* جدول حضور الموظفين اليوم */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h4 className="font-heading font-semibold text-primary flex items-center gap-2 text-xl">
                    <Clock className="w-6 h-6 text-gold" />
                    سجل حضور اليوم ({adminStats.today_attendance.length})
                  </h4>
                </div>
                {adminStats.today_attendance.length === 0 ? (
                  <p className="text-gray-500 text-base text-center py-8">لا يوجد سجلات حضور اليوم</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-4 text-right text-sm font-semibold text-gray-600">الموظف</th>
                          <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">وقت الدخول</th>
                          <th className="px-4 py-4 text-right text-sm font-semibold text-gray-600">موقع الدخول</th>
                          <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">وقت الخروج</th>
                          <th className="px-4 py-4 text-right text-sm font-semibold text-gray-600">موقع الخروج</th>
                          <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">الحالة</th>
                          <th className="px-4 py-4 text-center text-sm font-semibold text-gray-600">الساعات</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {adminStats.today_attendance.map((record, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold ${record.clock_out ? 'bg-gray-400' : 'bg-green-500'}`}>
                                  {record.user_name?.charAt(0)}
                                </div>
                                <span className="font-body text-base">{record.user_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center text-base text-gray-600">
                              {formatTime(record.clock_in)}
                            </td>
                            <td className="px-4 py-4 text-base text-gray-600">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span className="truncate max-w-[150px]">{formatLocation(record.clock_in_location)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center text-base text-gray-600">
                              {formatTime(record.clock_out)}
                            </td>
                            <td className="px-4 py-4 text-base text-gray-600">
                              {record.clock_out_location ? (
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span className="truncate max-w-[150px]">{formatLocation(record.clock_out_location)}</span>
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-4 text-center">
                              {record.clock_out ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
                                  <CheckCircle className="w-4 h-4" />
                                  انصرف
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-600 rounded-full text-sm">
                                  <AlertCircle className="w-4 h-4" />
                                  متواجد
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-center text-base font-semibold text-primary">
                              {record.total_hours ? `${record.total_hours} س` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* إجراءات سريعة */}
              <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-heading font-semibold text-primary mb-4">
                  إجراءات سريعة
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    data-testid="quick-create-user"
                    onClick={() => navigate('/create-user')}
                    className="p-4 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all flex flex-col items-center gap-2 font-body text-base"
                  >
                    <UserPlus className="w-6 h-6" />
                    <span>إنشاء مستخدم</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin/client-requests')}
                    className="p-4 bg-primary text-white rounded-lg hover:bg-primary-800 transition-all flex flex-col items-center gap-2 font-body text-base"
                  >
                    <Inbox className="w-6 h-6" />
                    <span>طلبات العملاء</span>
                  </button>
                  <button
                    onClick={() => navigate('/task-reports')}
                    className="p-4 border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-all flex flex-col items-center gap-2 font-body text-base"
                  >
                    <PieChart className="w-6 h-6" />
                    <span>تقارير المهام</span>
                  </button>
                  <button
                    onClick={() => navigate('/attendance')}
                    className="p-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all flex flex-col items-center gap-2 font-body text-base"
                  >
                    <Clock className="w-6 h-6" />
                    <span>سجل الحضور</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
